import { useEffect, useRef, useState } from 'react';

export function useResizeObserver() {
  const [width, setWidth] = useState<number>();
  const [height, setHeight] = useState<number>();
  const [contentRect, setContentRect] = useState<DOMRectReadOnly | undefined>();

  // Use a state entry for the observed node in order to update when the node changes. The state
  // setter can be directly reused as a callback ref.
  // https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  const [node, ref] = useState<Element | null>(null);

  // Init the ResizeObserver on mount and use the same instance throughout the hook's lifetime
  const observerRef = useRef<ResizeObserver>();
  useEffect(() => {
    if (observerRef.current) {
      return;
    }

    if (observerRef.current || !('ResizeObserver' in window)) {
      return;
    }

    observerRef.current = new ResizeObserver(entries => {
      for (const entry of entries) {
        const contentBoxSize = Array.isArray(entry.contentBoxSize)
          ? entry.contentBoxSize[0]
          : entry.contentBoxSize;
        setWidth(contentBoxSize?.inlineSize ?? entry.contentRect.width);
        setHeight(contentBoxSize?.blockSize ?? entry.contentRect.height);
        setContentRect(entry.contentRect);
      }
    });
  }, []);

  // Update the observed nodes when the node changes
  useEffect(() => {
    const resizeObserver = observerRef.current;
    if (!resizeObserver || !(node instanceof Element)) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    resizeObserver.observe(node);
    return () => resizeObserver.unobserve(node);
  }, [node]);

  return { width, height, ref, node, contentRect };
}
